import {
  LayoutDashboard,
  ScanLine,
  History,
  User,
  Megaphone,
  PlusCircle,
  BarChart3,
  Mail,
  Users,
  ShieldCheck,
  Receipt,
  Globe2,
  ArrowLeft,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { Logo } from '@/components/landing/Logo';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

export type Role = 'hotesse' | 'partenaire' | 'admin';

const navByRole: Record<
  Role,
  { label: string; items: { title: string; url: string; icon: typeof LayoutDashboard }[] }[]
> = {
  hotesse: [
    {
      label: 'Terrain',
      items: [
        { title: 'Tableau de bord', url: '/app', icon: LayoutDashboard },
        { title: 'Scanner', url: '/app/scanner', icon: ScanLine },
        { title: 'Historique', url: '/app/historique', icon: History },
        { title: 'Profil', url: '/app/profil', icon: User },
      ],
    },
  ],
  partenaire: [
    {
      label: 'Pilotage',
      items: [
        { title: 'Tableau de bord', url: '/partenaire', icon: LayoutDashboard },
        { title: 'Campagnes', url: '/partenaire/campagnes', icon: Megaphone },
        { title: 'Nouvelle campagne', url: '/partenaire/nouvelle', icon: PlusCircle },
        { title: 'Statistiques', url: '/partenaire/stats', icon: BarChart3 },
        { title: 'Leads', url: '/partenaire/leads', icon: Mail },
      ],
    },
  ],
  admin: [
    {
      label: 'Administration',
      items: [
        { title: "Vue d'ensemble", url: '/admin', icon: LayoutDashboard },
        { title: 'Utilisateurs', url: '/admin/utilisateurs', icon: Users },
        { title: 'Modération', url: '/admin/moderation', icon: ShieldCheck },
        { title: 'Facturation', url: '/admin/facturation', icon: Receipt },
        { title: 'Analytics géo', url: '/admin/analytics', icon: Globe2 },
      ],
    },
  ],
};

export const AppSidebar = ({ role }: { role: Role }) => {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const groups = navByRole[role];
  const isActive = (url: string) =>
    url === location.pathname ||
    (url !== '/app' &&
      url !== '/partenaire' &&
      url !== '/admin' &&
      location.pathname.startsWith(url));

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="border-b border-border px-3 py-4">
        {collapsed ? (
          <div className="grid place-items-center">
            <div className="grid size-9 place-items-center rounded-xl bg-gradient-primary font-display font-bold text-primary-foreground shadow-cta">
              Wi
            </div>
          </div>
        ) : (
          <Logo />
        )}
      </SidebarHeader>
      <SidebarContent>
        {groups.map(group => (
          <SidebarGroup key={group.label}>
            {!collapsed && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <NavLink
                        to={item.url}
                        end={
                          item.url === '/app' || item.url === '/partenaire' || item.url === '/admin'
                        }
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                        )}
                        activeClassName="bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
                      >
                        <item.icon className="size-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Retour landing">
                  <NavLink
                    to="/"
                    className="flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <ArrowLeft className="size-4 shrink-0" />
                    {!collapsed && <span>Retour landing</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};
